import { type CancellationReceipt, PLANS } from "@cva/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Field, Input, Select, Textarea } from "../components/ui/input";
import { orpc } from "../lib/api";
import { useSession } from "../lib/session";
import { errorMessage, formatDate } from "../lib/utils";
import { LegalLayout, useLegal } from "./legal";

/**
 * "Verträge hier kündigen" (§ 312k BGB): reachable without signing in, one form,
 * the button "Jetzt kündigen" and a confirmation that can be saved. Signed-in
 * owners pick the organization; everybody else is matched by the account's
 * email address (apps/backend/src/modules/cancellation.ts).
 */

const dateTime = (date: Date) =>
  date.toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" });

export function CancellationPage() {
  const legal = useLegal();
  const { data: session } = useSession();
  const cancellable = useQuery({
    ...orpc.billing.cancellable.queryOptions(),
    enabled: Boolean(session),
  });
  const [receipt, setReceipt] = useState<CancellationReceipt | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [kind, setKind] = useState<"ordinary" | "extraordinary">("ordinary");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!session) return;
    setName((value) => value || session.user.name);
    setEmail((value) => value || session.user.email);
  }, [session]);
  const choices = (cancellable.data ?? []).filter((item) => !item.endsAt);
  useEffect(() => {
    if (!orgId && choices[0]) setOrgId(choices[0].orgId);
  }, [orgId, choices]);

  const cancel = useMutation(
    orpc.billing.cancel.mutationOptions({ onSuccess: (data) => setReceipt(data) }),
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    cancel.mutate({
      name,
      email,
      kind,
      ...(reason.trim() ? { reason } : {}),
      ...(session && orgId ? { orgId } : { organizationName }),
    });
  };

  const alreadyCancelled = (cancellable.data ?? []).filter((item) => item.endsAt);

  return (
    <LegalLayout title="Verträge hier kündigen" page={legal.data}>
      {({ features }) =>
        receipt ? (
          <Receipt receipt={receipt} emailSent={features.smtp} />
        ) : (
          <>
            <p>
              Hier kündigst du dein Abo. Eine ordentliche Kündigung wirkt zum Ende des laufenden
              Abrechnungsmonats, in der Testphase zu deren Ende. Bis dahin kannst du wie gewohnt
              arbeiten; danach ist die Organisation schreibgeschützt, deine Inhalte bleiben
              erhalten.
            </p>
            {alreadyCancelled.map((item) => (
              <p key={item.orgId} className="rounded-lg bg-amber-50 px-4 py-3 text-amber-900">
                „{item.name}“ ist bereits gekündigt und endet am {formatDate(item.endsAt!)}.
              </p>
            ))}
            <form onSubmit={submit} className="not-prose mt-6 space-y-4">
              <Field label="Art der Kündigung">
                <Select
                  value={kind}
                  onChange={(event) => setKind(event.target.value as typeof kind)}
                >
                  <option value="ordinary">
                    Ordentliche Kündigung zum nächstmöglichen Zeitpunkt
                  </option>
                  <option value="extraordinary">
                    Außerordentliche Kündigung aus wichtigem Grund
                  </option>
                </Select>
              </Field>
              {kind === "extraordinary" ? (
                <Field label="Kündigungsgrund">
                  <Textarea
                    required
                    rows={4}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </Field>
              ) : null}
              <Field label="Dein Name">
                <Input
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
              <Field
                label="E-Mail-Adresse deines Kontos"
                hint="Daran erkennen wir den Vertrag. Die Eingangsbestätigung geht an diese Adresse."
              >
                <Input
                  required
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
              {session && choices.length > 0 ? (
                <Field label="Welches Abo (Organisation)?">
                  <Select value={orgId} onChange={(event) => setOrgId(event.target.value)}>
                    {choices.map((item) => (
                      <option key={item.orgId} value={item.orgId}>
                        {item.name} · Tarif {PLANS[item.plan].name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field
                  label="Organisation"
                  hint="Der Name der Organisation, deren Abo du kündigen willst. Nur nötig, wenn du mehrere hast."
                >
                  <Input
                    value={organizationName}
                    onChange={(event) => setOrganizationName(event.target.value)}
                  />
                </Field>
              )}
              {cancel.error ? (
                <p className="text-sm text-red-600">{errorMessage(cancel.error)}</p>
              ) : null}
              <Button type="submit" variant="danger" size="lg" loading={cancel.isPending}>
                Jetzt kündigen
              </Button>
            </form>
          </>
        )
      }
    </LegalLayout>
  );
}

/** Confirmation with date and time, printable / savable (§ 312k Abs. 4 BGB). */
function Receipt({ receipt, emailSent }: { receipt: CancellationReceipt; emailSent: boolean }) {
  return (
    <>
      <p className="rounded-lg bg-emerald-50 px-4 py-3 font-medium text-emerald-900">
        Deine Kündigung ist eingegangen.
      </p>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
        <dt className="font-semibold text-zinc-900">Eingegangen am</dt>
        <dd>{dateTime(receipt.createdAt)} Uhr</dd>
        <dt className="font-semibold text-zinc-900">Art</dt>
        <dd>
          {receipt.kind === "ordinary"
            ? "Ordentliche Kündigung zum nächstmöglichen Zeitpunkt"
            : "Außerordentliche Kündigung"}
        </dd>
        <dt className="font-semibold text-zinc-900">Name</dt>
        <dd>{receipt.name}</dd>
        <dt className="font-semibold text-zinc-900">E-Mail</dt>
        <dd>{receipt.email}</dd>
        {receipt.organizationName ? (
          <>
            <dt className="font-semibold text-zinc-900">Organisation</dt>
            <dd>{receipt.organizationName}</dd>
          </>
        ) : null}
        <dt className="font-semibold text-zinc-900">Wirksam zum</dt>
        <dd>
          {receipt.status === "cancelled" && receipt.endsAt
            ? `${formatDate(receipt.endsAt)} (Ende der Laufzeit)`
            : "Wir ordnen die Kündigung deinem Vertrag zu und bestätigen dir den Zeitpunkt."}
        </dd>
        <dt className="font-semibold text-zinc-900">Vorgangsnummer</dt>
        <dd className="font-mono text-sm">{receipt.id}</dd>
      </dl>
      <p>
        {emailSent
          ? `Eine Bestätigung haben wir an ${receipt.email} geschickt.`
          : "Bitte speichere oder drucke diese Bestätigung."}{" "}
        Bis zum Ende der Laufzeit kannst du wie gewohnt weiterarbeiten.
      </p>
      <Button variant="secondary" onClick={() => window.print()} className="print:hidden">
        <Printer /> Bestätigung drucken oder als PDF speichern
      </Button>
    </>
  );
}
